import { useState, useEffect } from 'react'
import { Camera } from 'lucide-react'

interface Props {
  userId: string
  name: string
  hasFace: boolean
  proxyUrl?: string
  proxyToken?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showUploadHint?: boolean
  onClick?: () => void
  onOpenUpload?: () => void
}

const SIZE = {
  sm: { wrap: 'w-8 h-8',   text: 'text-[10px]', icon: 'w-3 h-3' },
  md: { wrap: 'w-14 h-14', text: 'text-lg',      icon: 'w-5 h-5' },
  lg: { wrap: 'w-20 h-20', text: 'text-2xl',     icon: 'w-6 h-6' },
  xl: { wrap: 'w-28 h-28', text: 'text-3xl',     icon: 'w-8 h-8' },
}

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()
}

export function FaceAvatar({ userId, name, hasFace, proxyUrl, proxyToken, size = 'md', showUploadHint, onClick, onOpenUpload }: Props) {
  const [src, setSrc]       = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const s = SIZE[size]

  const handleClick = onOpenUpload ?? onClick

  useEffect(() => {
    if (!hasFace || !proxyUrl || !proxyToken) { setSrc(null); return }
    setLoading(true)
    let url: string | null = null
    fetch(`${proxyUrl}/faces/user/${userId}`, { headers: { Authorization: `Bearer ${proxyToken}` } })
      .then(r => r.ok ? r.blob() : null)
      .then(blob => { if (!blob) return; url = URL.createObjectURL(blob); setSrc(url) })
      .catch(() => {})
      .finally(() => setLoading(false))
    return () => { if (url) URL.revokeObjectURL(url) }
  }, [userId, hasFace, proxyUrl, proxyToken])

  const base     = `${s.wrap} rounded-full overflow-hidden shrink-0 flex items-center justify-center relative`
  const clickable = handleClick ? 'cursor-pointer group' : ''

  if (loading) {
    return (
      <div className={`${base} bg-accent/10 border-2 border-accent/20 animate-pulse ${clickable}`} onClick={handleClick}>
        <span className={`text-accent font-bold ${s.text}`}>{initials(name)}</span>
      </div>
    )
  }

  if (src) {
    return (
      <div className={`${base} border-2 border-accent/40 ${clickable}`} onClick={handleClick}>
        <img src={src} alt={name} className="w-full h-full object-cover" />
        {onOpenUpload && (
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
            <Camera className={`${s.icon} text-white`} />
            <span className="text-white text-[9px] font-medium leading-none">Cambiar</span>
          </div>
        )}
      </div>
    )
  }

  if (showUploadHint || onOpenUpload) {
    return (
      <div className={`${base} bg-surface border-2 border-dashed border-border flex-col gap-1 ${clickable}`} onClick={handleClick}>
        <Camera className={`${s.icon} text-text-muted group-hover:text-accent transition-colors`} />
        <span className="text-[9px] text-text-muted group-hover:text-accent transition-colors leading-tight text-center px-1">Agregar foto</span>
      </div>
    )
  }

  return (
    <div className={`${base} bg-accent/15 border-2 border-accent/25 ${clickable}`} onClick={handleClick}>
      <span className={`text-accent font-bold ${s.text}`}>{initials(name)}</span>
    </div>
  )
}
